import { useState } from "react";
import type { PoolInfo } from "../types/pool";
import "./PoolDetails.css";

const HOOK_PRESETS = ["Volatility Fee", "Loyalty", "Oracle Seeding"];

interface PoolDetailsProps {
  pool: PoolInfo;
  onAddLiquidity: () => void;
  onBack: () => void;
}

export function PoolDetails({ pool, onAddLiquidity, onBack }: PoolDetailsProps) {
  const [selectedHooks, setSelectedHooks] = useState<string[]>([]);
  const [showHookDropdown, setShowHookDropdown] = useState(false);
  const [registeredHooks, setRegisteredHooks] = useState<string[]>(pool.hooks);

  const toggleHook = (name: string) => {
    if (selectedHooks.includes(name)) {
      setSelectedHooks(selectedHooks.filter((h) => h !== name));
    } else {
      setSelectedHooks([...selectedHooks, name]);
    }
  };

  const registerHooks = () => {
    setRegisteredHooks([...registeredHooks, ...selectedHooks]);
    setSelectedHooks([]);
    setShowHookDropdown(false);
  };

  return (
    <div className="pool-details screen">
      <header className="form-header">
        <button type="button" className="back-btn" onClick={onBack}>
          ← Back
        </button>
        <h1>Pool Details</h1>
      </header>

      <div className="pool-details-card">
        <h2>
          Pool ID: <code>{pool.poolId}</code>
        </h2>
        <dl className="pool-meta">
          <dt>Tokens</dt>
          <dd>
            {pool.tokenA} / {pool.tokenB}
          </dd>
          <dt>Mode</dt>
          <dd>{pool.poolMode}</dd>
          <dt>Fee</dt>
          <dd>
            {pool.feeTierPct}%
            {pool.feeRebatePct != null && pool.feeRebatePct > 0 && (
              <span className="fee-rebate-badge">
                {" "}
                (volume-tier rebate: up to {pool.feeRebatePct}% at highest
                tier; settled every 2 weeks)
              </span>
            )}
          </dd>
          <dt>Tick Spacing</dt>
          <dd>{pool.tickSpacing}</dd>
          <dt>ReClamm</dt>
          <dd>
            Margin {pool.reclamm.marginPct}% · Exp {pool.reclamm.exponentPct}% · Sigma {pool.reclamm.sigmaPct}%
            {pool.reclamm.autoGlide && " · Auto Glide"}
          </dd>
          <dt>Boosted</dt>
          <dd>{pool.boosted ? "Yes" : "No"}</dd>
        </dl>

        <section className="hooks-section">
          <h3>Hooks</h3>
          {registeredHooks.length > 0 ? (
            <ul className="hooks-list">
              {registeredHooks.map((h) => (
                <li key={h}>{h}</li>
              ))}
            </ul>
          ) : (
            <p className="hooks-none">None added</p>
          )}
          <div className="hooks-actions">
            <div className="dropdown-wrap">
              <button
                type="button"
                className="add-hooks-btn"
                onClick={() => setShowHookDropdown(!showHookDropdown)}
              >
                Add Hooks
              </button>
              {showHookDropdown && (
                <>
                  <div
                    className="dropdown-backdrop"
                    onClick={() => setShowHookDropdown(false)}
                    aria-hidden
                  />
                  <div className="dropdown-menu">
                    {HOOK_PRESETS.map((name) => (
                      <label key={name} className="dropdown-item">
                        <input
                          type="checkbox"
                          checked={selectedHooks.includes(name)}
                          onChange={() => toggleHook(name)}
                        />
                        {name}
                      </label>
                    ))}
                    <button
                      type="button"
                      className="register-hooks-btn"
                      onClick={registerHooks}
                    >
                      Register Selected Hooks
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        <div className="pool-details-actions">
          <button type="button" className="secondary" onClick={onBack}>
            Back to list
          </button>
          <button type="button" className="primary" onClick={onAddLiquidity}>
            Add Liquidity
          </button>
        </div>
      </div>
    </div>
  );
}
